import { formatAddress, type AddressInfo } from '@/shared/composables/useAddressFormatter';

export const SG_ADDR_LOCAL = 'B07001';
export const SG_ADDR_FOREIGN = 'B07002';

export type ShareholderAddressSlot = 'residential' | 'contact';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

export function isSgForeignAddressType(code: unknown): boolean {
  const v = trim(code).toUpperCase();
  return v === SG_ADDR_FOREIGN || v === 'F';
}

export function isSgLocalAddressType(code: unknown): boolean {
  return !isSgForeignAddressType(code);
}

/** Residential (primary) — Share Application applicant_address */
export function shareholderResidentialAddressInfo(row: Record<string, unknown>): AddressInfo {
  return {
    ADDRESS_TYPE_CD: trim(row.addressType) || SG_ADDR_LOCAL,
    POSTAL_CODE: trim(row.postalCode),
    BLOCK_NO: trim(row.blockNo),
    STREET_NAME: trim(row.streetName),
    BUILDING_NAME: trim(row.buildingName),
    ADDR_LEVEL: trim(row.levelNo),
    ADDR_UNIT: trim(row.unitNo),
    FOREIGN_ADDRESS_LINE_1: trim(row.foreignAddr1),
    FOREIGN_ADDRESS_LINE_2: trim(row.foreignAddr2),
  };
}

/** Contact / alternate address — eFiling contact* fields */
export function shareholderContactAddressInfo(row: Record<string, unknown>): AddressInfo {
  const typeCd = trim(row.alternateAddressCd) || trim(row.addressType) || SG_ADDR_LOCAL;
  return {
    ADDRESS_TYPE_CD: typeCd,
    POSTAL_CODE: trim(row.altPostalCode),
    BLOCK_NO: trim(row.altBlockNo),
    STREET_NAME: trim(row.altStreetName),
    BUILDING_NAME: trim(row.altBuildingName),
    ADDR_LEVEL: trim(row.altAddrLevel),
    ADDR_UNIT: trim(row.altAddrUnit),
    FOREIGN_ADDRESS_LINE_1: trim(row.altForeignAddr1),
    FOREIGN_ADDRESS_LINE_2: trim(row.altForeignAddr2),
  };
}

export function composeApplicantAddress(row: Record<string, unknown>): string {
  const info = shareholderResidentialAddressInfo(row);
  const formatted = formatAddress(info, { useComma: true });
  if (typeof formatted === 'string' && formatted.trim()) return formatted.trim();
  if (isSgForeignAddressType(info.ADDRESS_TYPE_CD)) return '';
  return trim(row.primaryAddress) || trim(row.address);
}

export function syncShareholderPrimaryAddressLine(row: Record<string, unknown>): void {
  row.primaryAddress = composeApplicantAddress(row);
}

/** One-line display for active Residential / Contact Address chip. */
export function formatShareholderAddressLine(
  row: Record<string, unknown>,
  slot: ShareholderAddressSlot,
): string {
  const info =
    slot === 'contact' ? shareholderContactAddressInfo(row) : shareholderResidentialAddressInfo(row);
  const formatted = formatAddress(info, { useComma: true });
  if (typeof formatted === 'string' && formatted.trim()) return formatted.trim();
  const typeCd = trim(info.ADDRESS_TYPE_CD);
  if (isSgForeignAddressType(typeCd)) return '';
  if (slot === 'residential' && isSgLocalAddressType(typeCd)) {
    return trim(row.primaryAddress) || trim(row.address);
  }
  return '';
}

/** Preset card chip — show only when slot has displayable address (same rule as formatShareholderAddressLine). */
export function hasShareholderAddressSlotData(
  row: Record<string, unknown>,
  slot: ShareholderAddressSlot,
): boolean {
  return !!formatShareholderAddressLine(row, slot).trim();
}

/** Card Address field — explicit refresh after dialog Done / chip / contact bind. */
export function refreshShareholderAddressDisplay(row: Record<string, unknown>): void {
  const slot = normalizeShareholderAddressSlot(row.activeAddressSlot);
  row.addressDisplayLine = formatShareholderAddressLine(row, slot);
}

export function normalizeShareholderAddressSlot(value: unknown): ShareholderAddressSlot {
  return trim(value) === 'contact' ? 'contact' : 'residential';
}

/** TASK_DATA shareholders[] round-trip */
export function serializeShareholderAddressFields(row: Record<string, unknown>): Record<string, string> {
  return {
    activeAddressSlot: normalizeShareholderAddressSlot(row.activeAddressSlot),
    addressType: trim(row.addressType),
    postalCode: trim(row.postalCode),
    blockNo: trim(row.blockNo),
    streetName: trim(row.streetName),
    buildingName: trim(row.buildingName),
    levelNo: trim(row.levelNo),
    unitNo: trim(row.unitNo),
    foreignAddr1: trim(row.foreignAddr1),
    foreignAddr2: trim(row.foreignAddr2),
    alternateAddressCd: trim(row.alternateAddressCd),
    altPostalCode: trim(row.altPostalCode),
    altBlockNo: trim(row.altBlockNo),
    altStreetName: trim(row.altStreetName),
    altBuildingName: trim(row.altBuildingName),
    altAddrLevel: trim(row.altAddrLevel),
    altAddrUnit: trim(row.altAddrUnit),
    altForeignAddr1: trim(row.altForeignAddr1),
    altForeignAddr2: trim(row.altForeignAddr2),
    primaryAddress: trim(row.primaryAddress),
    addressDisplayLine: trim(row.addressDisplayLine),
  };
}

export function applySerializedShareholderAddressFields(
  row: Record<string, unknown>,
  source: Record<string, unknown>,
): void {
  const fields = serializeShareholderAddressFields(source);
  for (const [key, value] of Object.entries(fields)) {
    row[key] = value;
  }
  row.activeAddressSlot = normalizeShareholderAddressSlot(row.activeAddressSlot);
  refreshShareholderAddressDisplay(row);
}
